import * as mobx from 'mobx';
import * as IoC from 'typescript-ioc';
import { IntlShape, MessageDescriptor } from 'react-intl';
import { AbstractFormViewModelController } from '../../../../../infrastructure/implementations/form-viewmodel-controller';
import { IEventDetailsDTO } from '../../../../../api/events.dao';
import {
    ISalesforceAddressDTO,
    ISalesforceContactDTO,
    ISalesforceLookupDTO,
    ISalesforceOrderCreateEvent,
    ISalesforceOrderCreatePayload,
    ISalesforceOrderCreateProduct,
    ISalesforceOrderCreateResult,
    ISalesforceOrderDAO,
    ISalesforceOrderDTO,
    ISalesforceOrderFormDataDTO,
    ISalesforceOrderProductDTO,
    IRecentOrderDTO,
} from '../../../../../api/salesforce-order.dao';
import { IBlueprintsDAO } from '../../../../../api/blueprints/blueprints.dao';
import { ILocalStorageResponse, ILocalStorageService } from '../../../../../infrastructure/local-storage-service';
import { IReactIntlProvider } from '../../../../../infrastructure/react-intl-provider';
import { IErrorParsing } from '../../../../../infrastructure/error-parsing';
import { IResponseBase } from '../../../../../infrastructure/api-client';
import { formatTimezoneOffset, getTimezoneOffsetInMinutes } from '../../../../../infrastructure/utils/date-fns-overrides.utils';
import { DateFnsFormats, IDateTimeFormatter, TimeFormats } from '../../../../../infrastructure/date-formatter';
import { timezonesCodeLabelMap } from '../../../../../infrastructure/timezones-code-label-map';
import TrackingServiceProvider from '../../../../../tracking-service/tracking-service-provider';
import { SALESFORCE_ORDER_TRACKING_EVENTS } from '../../../../../tracking-service/tracking-values';
import { showNotification } from '../../../../global/modal/alerts-host/alert-templates';
import { ADD_ORDER_FORM_CONFIG, DEFAULT_BILLING_STATUS, DEFAULT_CURRENCY, DEFAULT_ORDER_STATUS, LOCKED_BILLING_STATUSES, ORDER_SUBTYPE_BY_TYPE } from './add-salesforce-order.fields';
import { FormFieldProps, FormVmValueType, InitialData } from '../../../../../infrastructure/form-viewmodel-controller';
import { AddSalesforceOrderTrackingMapper } from './add-salesforce-order-tracking.mapper';
import { resolveSalesforceProjectManagers } from './salesforce-shared-fields-tracking.mapper';
import { ManageProductsTrackingMapper } from './manage-products-tracking.mapper';
import { IManageProductsSelection, makeRow, ManageProductsViewModel } from './manage-products.viewmodel';
import { mapOrderToFormValues } from './order-to-form-values.mapper';
import { resolveLinkedEventFields, toNumber } from './salesforce-order-display.mapper';
import {
    STANDARD_ORDER_RECORD_TYPE,
    getRecordTypeFromOrder,
    isLifecycleFieldKey,
    isNoContractRequiredRecordType,
    resolveRecordTypeName,
    toAllowedRecordTypeName,
} from './order-field-visibility';
import { IOrderFormFieldsVM } from './order-form-fields.types';
import {
    booleanValue as readBooleanValue,
    createLookupFieldDispatch,
    getCachedOpportunitySearch,
    salesforceAccountSearchCacheKey,
    salesforceOpportunitySearchCacheKey,
    searchOpportunitiesWithCache,
    SF_ACCOUNT_SEARCH_CACHE_MINUTES,
    stringValue as readStringValue,
    toIdNameOptions,
} from './order-form-values';
import {
    applyBillingMethodChange,
    applyCancellationReasonChange,
    applyHighProfileOrderChange,
    applyHighProfileReasoningChange,
    applyOrderTypeChange,
} from './order-field-dependencies';
import {
    EXISTING_ORDER_CLONE_ERROR_TEXT,
    EXISTING_ORDER_LINK_LOAD_ERROR_TEXT,
    EXISTING_ORDER_REQUIRED_MESSAGE,
    EXISTING_ORDER_ALREADY_LINKED_MESSAGE,
    MANAGE_PRODUCTS_NO_PRICE_BOOK_TEXT,
    ORDER_ADDED_TEXT,
    ORDER_CREATE_ERROR_TEXT,
    ORDER_LINKED_TEXT,
    ORDER_LINK_ERROR_TEXT,
    ORDER_UPDATED_TEXT,
    ORDER_UPDATE_ERROR_TEXT,
} from './salesforce-order.texts';
import { ILookupColumn, ILookupOption, LOOKUP_COLUMN_KEY } from './lookup-select';
import { hasPermission } from '../../../../../infrastructure/utils/has-permissions';
import { Permission } from '../../../../../api/roles.dao';

const EMPTY = '—';

export type SalesforceOrderFormMode = 'create' | 'edit';
export type OrderCreationMode = 'manual' | 'clone' | 'link';

const mapProductsToSelections = (
    products: ISalesforceOrderProductDTO[],
    fallbackCurrency: string,
): IManageProductsSelection[] =>
    products.map(product => makeRow(
        {
            pricebookEntryId: '',
            productName: product.productName,
            currency: product.currency ?? fallbackCurrency,
            listPrice: toNumber(product.listPrice),
        },
        {
            lineItemId: product.lineItemId ?? '',
            unitPrice: toNumber(product.unitPrice),
            notes: product.notes ?? '',
            quantity: toNumber(product.quantity) ?? 0,
            checked: true,
        },
    ));

interface ISalesforceOrderLookupOption extends ILookupOption {
    orderNumber?: string | null;
    oecEventId?: string | null;
    oeCentralUrl?: string | null;
}

type ISelectOption = ISalesforceOrderLookupOption;

interface IAccountSearchCache {
    term: string;
    accounts: { id: string; name: string }[];
}


interface IRecentOrderCache {
    term: string;
    orders: {
        id: string;
        label: string;
        columns: ILookupColumn[];
        orderNumber?: string | null;
        oecEventId?: string | null;
        oeCentralUrl?: string | null;
    }[];
}

const recentOrderSearchCacheKey = (eventId: number): string => `sf-recent-order-search-v4-${eventId}`;
const SF_RECENT_ORDER_SEARCH_CACHE_MINUTES = 15;

const formatOrderDate = (isoDate: string): string => {
    if (!isoDate) {
        return '';
    }
    const [year, month, day] = isoDate.split('-').map(Number);
    if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
        return '';
    }
    const date = new Date(year, month - 1, day);
    const dateFormatter = IoC.Container.get(IDateTimeFormatter);
    const is24Hours = dateFormatter.getLocalTimeFormat() === TimeFormats.Day24Hours;
    const format = is24Hours ? DateFnsFormats.ShortDayMonthYear24Hours : DateFnsFormats.ShortDayMonthYear12Hours;
    return dateFormatter.dateToFormattedString(date, format);
};

const formatOrderDateRange = (order: Pick<IRecentOrderDTO, 'startDate' | 'endDate'>): string => {
    const start = formatOrderDate(order.startDate);
    const end = formatOrderDate(order.endDate);
    if (!start || !end || start === end) {
        return start || end;
    }
    return `${start} - ${end}`;
};

const blankColumn = (value: string | null | undefined): string => value ?? '';

const recentOrderColumns = (order: IRecentOrderDTO): ILookupColumn[] =>
    [
        { key: LOOKUP_COLUMN_KEY.eventName, value: order.name },
        { key: LOOKUP_COLUMN_KEY.requesterName, value: blankColumn(order.requesterName) },
        { key: LOOKUP_COLUMN_KEY.eventType, value: blankColumn(order.eventType) },
        { key: LOOKUP_COLUMN_KEY.orderNumber, value: `OE ${order.orderNumber}` },
        { key: LOOKUP_COLUMN_KEY.dates, value: formatOrderDateRange(order) },
    ];

const formatRecentOrderLabel = (order: IRecentOrderDTO): string =>
    `${order.name} — OE ${order.orderNumber} (${formatOrderDateRange(order)})`;

const isLinkedFieldSet = (value?: string | number | null): boolean =>
    value != null && String(value).trim() !== '';

const orderNumberFromOption = (option?: Pick<ISalesforceOrderLookupOption, 'orderNumber'> | null): string =>
    option?.orderNumber?.trim() ?? '';

type LinkedFieldBag = {
    oecEventId?: string | number | null;
    oeCentralUrl?: string | number | null;
    oecUrl?: string | number | null;
};

const isOrderAlreadyLinked = (order?: (LinkedFieldBag & { order?: LinkedFieldBag | null }) | null): boolean => {
    if (!order) {
        return false;
    }
    return isLinkedFieldSet(resolveLinkedEventFields(order).oecEventId);
};

const linkedFields = (order: LinkedFieldBag & { order?: LinkedFieldBag | null }): Pick<ISalesforceOrderLookupOption, 'oecEventId' | 'oeCentralUrl'> => {
    const resolved = resolveLinkedEventFields(order);
    const fields: Pick<ISalesforceOrderLookupOption, 'oecEventId' | 'oeCentralUrl'> = {};
    if (isLinkedFieldSet(resolved.oecEventId)) {
        fields.oecEventId = String(resolved.oecEventId);
    }
    if (isLinkedFieldSet(resolved.oeCentralUrl)) {
        fields.oeCentralUrl = String(resolved.oeCentralUrl);
    }
    return fields;
};

const recentOrderOptionFields = (order: IRecentOrderDTO) => ({
    label: formatRecentOrderLabel(order),
    columns: recentOrderColumns(order),
    orderNumber: order.orderNumber,
    ...linkedFields(order),
});

const toRecentOrderOption = (order: IRecentOrderDTO): ISelectOption => ({
    value: order.id,
    ...recentOrderOptionFields(order),
});

const toCachedRecentOrder = (order: IRecentOrderDTO): IRecentOrderCache['orders'][number] => ({
    id: order.id,
    ...recentOrderOptionFields(order),
});

const toLookupOptionFromCache = (order: IRecentOrderCache['orders'][number]): ISelectOption => ({
    value: order.id,
    label: order.label,
    columns: order.columns,
    ...(order.orderNumber ? { orderNumber: order.orderNumber } : {}),
    ...linkedFields(order),
});

class AddSalesforceOrderFormViewModel extends AbstractFormViewModelController implements IOrderFormFieldsVM {
    @mobx.observable
    private saving: boolean = false;
    @mobx.observable
    private blueprintName: string | null = null;
    @mobx.observable
    private formData: ISalesforceOrderFormDataDTO | null = null;
    @mobx.observable
    private loadingFormData: boolean = false;
    @mobx.observable
    private products: IManageProductsSelection[] = [];
    @mobx.observable
    private priceBookId: string | null = null;
    @mobx.observable
    public isManageProductsOpen: boolean = false;
    @mobx.observable
    private manageProductsVm: ManageProductsViewModel | null = null;
    @mobx.observable
    private saveErrorMessage: string | null = null;
    @mobx.observable
    public creationMode: OrderCreationMode = 'clone';
    @mobx.observable
    private existingOrderLoading: boolean = false;
    @mobx.observable
    private existingOrderValue: string = '';
    @mobx.observable
    private existingOrderTouched: boolean = false;
    @mobx.observable
    private linkedOrder: ISalesforceOrderDTO | null = null;
    @mobx.observable
    private selectedOrderAlreadyLinked: boolean = false;
    @mobx.observable
    private selectedOrderNumber: string = '';
    @mobx.observable
    private corporateRelatedOption: ISelectOption | null = null;
    @mobx.observable
    private billToLegalEntityOption: ISelectOption | null = null;
    @mobx.observable
    private channelAgencyReferralOption: ISelectOption | null = null;
    @mobx.observable
    private opportunityOption: ISelectOption | null = null;
    private opportunitySearchRequestId = 0;
    @mobx.observable
    private recordType: string = STANDARD_ORDER_RECORD_TYPE;
    private hasSalesforceRecordType: boolean = false;

    public readonly event: IEventDetailsDTO;
    private readonly accountId: number;
    private readonly onCreated: (result: ISalesforceOrderCreateResult) => void;
    public readonly mode: SalesforceOrderFormMode;
    private readonly order: ISalesforceOrderDTO | null;
    @mobx.observable
    private loadedBillingStatus: string | null = null;

    constructor(
        event: IEventDetailsDTO,
        accountId: number,
        onCreated: (result: ISalesforceOrderCreateResult) => void = () => undefined,
        mode: SalesforceOrderFormMode = 'create',
        order: ISalesforceOrderDTO | null = null,
        @IoC.Inject private readonly salesforceOrderDAO: ISalesforceOrderDAO = IoC.Container.get(ISalesforceOrderDAO),
        @IoC.Inject private readonly trackingService: TrackingServiceProvider = IoC.Container.get(TrackingServiceProvider),
        @IoC.Inject private readonly intlProvider: IReactIntlProvider = IoC.Container.get(IReactIntlProvider),
        @IoC.Inject private readonly blueprintsDAO: IBlueprintsDAO = IoC.Container.get(IBlueprintsDAO),
        @IoC.Inject private readonly localStorageService: ILocalStorageService = IoC.Container.get(ILocalStorageService),
        @IoC.Inject private readonly errorParsing: IErrorParsing = IoC.Container.get(IErrorParsing),
    ) {
        const initialValues: InitialData = mode === 'edit' && order?.order ? mapOrderToFormValues(order.order) : {};
        super(initialValues, 'AddSalesforceOrder', ADD_ORDER_FORM_CONFIG);
        this.event = event;
        this.accountId = accountId;
        this.onCreated = onCreated;
        this.mode = mode;
        this.order = order;
        this.loadedBillingStatus = mode === 'edit' ? (order?.order?.billingStatus ?? null) : null;
        if (mode === 'edit' && order?.order) {
            void this.applyOrderToForm(order);
        }
        this.loadBlueprintName();
        this.loadFormData();
    }


    @mobx.action
    private async applyOrderToForm(order: ISalesforceOrderDTO): Promise<void> {
        if (!order.order) {
            return;
        }
        const values = mapOrderToFormValues(order.order);
        if (this.mode === 'edit') {
            this.recordType = getRecordTypeFromOrder(order.order);
            this.hasSalesforceRecordType = resolveRecordTypeName(order.order) != null;
        }
        const isCloningIntoNewOrder = this.mode === 'create';
        Object.keys(values)
            .filter(key => !(isCloningIntoNewOrder && (isLifecycleFieldKey(key) || key === 'feedbackFormUrl')))
            .forEach(key => this.setValue(key, values[key]));

        // For new orders (even when cloning), reset status fields to defaults
        if (isCloningIntoNewOrder) {
            this.setValue('orderStatus', DEFAULT_ORDER_STATUS);
            this.setValue('billingStatus', DEFAULT_BILLING_STATUS);
        }

        const related = order.order.corporateRelated;
        this.corporateRelatedOption = related?.id ? { value: related.id, label: related.name } : null;
        const billTo = order.order.billToLegalEntity;
        this.billToLegalEntityOption = billTo?.id ? { value: billTo.id, label: billTo.name } : null;
        const referral = order.order.channelAgencyReferral;
        this.channelAgencyReferralOption = referral?.id ? { value: referral.id, label: referral.name } : null;
        const opportunity = order.order.opportunityName;
        this.opportunityOption = opportunity?.id ? { value: opportunity.id, label: opportunity.name } : null;
        this.priceBookId = order.order.pricebook?.id ?? null;
        if (this.mode !== 'edit') {
            this.products = [];
            return;
        }
        this.products = mapProductsToSelections(order.products ?? [], order.order.currencyIsoCode ?? DEFAULT_CURRENCY);
        await this.resolveEditProductIds(order);
    }

    @mobx.action
    private async resolveEditProductIds(order: ISalesforceOrderDTO): Promise<void> {
        const priceBookId = order.order?.pricebook?.id;
        if (!priceBookId || this.products.length === 0) {
            return;
        }
        try {
            const response = await this.salesforceOrderDAO.getPriceBookProducts({
                priceBookId: priceBookId,
                contractId: this.stringValue('contractNumber') || null,
            });
            const currency = order.order?.currencyIsoCode ?? DEFAULT_CURRENCY;
            const idByName = new Map(
                (response?.products ?? [])
                    .filter(product => product.currencyIsoCode === currency)
                    .map(product => [product.productName, product.pricebookEntryId] as const),
            );
            this.applyResolvedProductIds(idByName);
        } catch {
            // Keep the placeholder ids if the catalogue cannot be loaded.
        }
    }

    @mobx.action
    private applyResolvedProductIds(idByName: Map<string, string>): void {
        this.products = this.products.map(product => {
            const resolvedId = idByName.get(product.productName);
            return resolvedId ? { ...product, pricebookEntryId: resolvedId } : product;
        });
    }

    public get isEditMode(): boolean {
        return this.mode === 'edit';
    }

    public getOrderNumber(): string {
        return this.event?.orderId ?? '';
    }

    public getOeNumber(): string {
        return this.event?.oeNumber ?? '';
    }

    public getRecordType(): string {
        return this.recordType;
    }

    public searchAccounts = async (fieldKey: string, term: string): Promise<ISelectOption[]> => {
        const cacheKey = salesforceAccountSearchCacheKey(this.event.eventId, fieldKey);
        const cached = this.localStorageService.getLocalStorageValue<ILocalStorageResponse>(cacheKey);
        if (cached && cached.value && this.localStorageService.checkTimeRange(cached.creationDate, cacheKey, SF_ACCOUNT_SEARCH_CACHE_MINUTES)) {
            const entry = cached.value as IAccountSearchCache;
            if (entry.term === term) {
                return entry.accounts.map(account => ({ value: account.id, label: account.name }));
            }
        }

        const accounts = await this.salesforceOrderDAO.searchAccounts({ accountId: this.accountId, searchTerm: term });
        this.localStorageService.setLocalStorageValue({ name: cacheKey, value: { term: term, accounts: accounts }, creationDate: Date.now() });
        return accounts.map(account => ({ value: account.id, label: account.name }));
    };

    public getCachedAccounts = (fieldKey: string): { term: string; options: ISelectOption[] } | null => {
        const cacheKey = salesforceAccountSearchCacheKey(this.event.eventId, fieldKey);
        const cached = this.localStorageService.getLocalStorageValue<ILocalStorageResponse>(cacheKey);
        if (cached && cached.value && this.localStorageService.checkTimeRange(cached.creationDate, cacheKey, SF_ACCOUNT_SEARCH_CACHE_MINUTES)) {
            const entry = cached.value as IAccountSearchCache;
            return { term: entry.term, options: entry.accounts.map(account => ({ value: account.id, label: account.name })) };
        }
        return null;
    };

    public searchOpportunities = async (term: string): Promise<ISelectOption[]> => {
        const requestId = ++this.opportunitySearchRequestId;
        const cacheKey = salesforceOpportunitySearchCacheKey(this.event.eventId);
        return searchOpportunitiesWithCache(
            this.localStorageService,
            this.salesforceOrderDAO,
            cacheKey,
            this.accountId,
            term,
            () => this.opportunitySearchRequestId === requestId,
        );
    };

    public getCachedOpportunities = (): { term: string; options: ISelectOption[] } | null =>
        getCachedOpportunitySearch(this.localStorageService, salesforceOpportunitySearchCacheKey(this.event.eventId));

    public searchCorporateAccounts = (term: string): Promise<ISelectOption[]> =>
        this.searchAccounts('corporateRelated', term);

    public getCachedCorporateAccounts = (): { term: string; options: ISelectOption[] } | null =>
        this.getCachedAccounts('corporateRelated');

    @mobx.action.bound
    public setCreationMode(mode: OrderCreationMode): void {
        if (mode === this.creationMode) {
            return;
        }
        this.creationMode = mode;
        this.cancelExistingOrderRequest();
        this.resetVm();
        this.existingOrderValue = '';
        this.existingOrderTouched = false;
        this.setLinkedOrder(null);
        this.setSelectedOrderAlreadyLinked(false);
        this.selectedOrderNumber = '';
        this.corporateRelatedOption = null;
        this.billToLegalEntityOption = null;
        this.channelAgencyReferralOption = null;
        this.opportunityOption = null;
        this.products = [];
        this.priceBookId = null;
        this.manageProductsVm = null;
    }

    public get isExistingOrderLoading(): boolean {
        return this.existingOrderLoading;
    }

    public get isLinkMode(): boolean {
        return this.mode === 'create' && this.creationMode === 'link';
    }

    @mobx.computed
    public get linkedOrderPreview(): ISalesforceOrderDTO | null {
        return this.linkedOrder;
    }

    @mobx.action
    private setLinkedOrder(order: ISalesforceOrderDTO | null): void {
        this.linkedOrder = order;
    }

    @mobx.action
    private setSelectedOrderAlreadyLinked(value: boolean): void {
        this.selectedOrderAlreadyLinked = value;
    }

    public get isLinkedOrderAlreadyLinked(): boolean {
        return this.isLinkMode && this.selectedOrderAlreadyLinked;
    }

    public get isCloneFieldsLocked(): boolean {
        return this.mode === 'create' && this.creationMode === 'clone' && (!this.existingOrderValue || this.existingOrderLoading);
    }

    public get isBillingLocked(): boolean {
        if (hasPermission(Permission.OVERRIDE_BILLING_LOCK)) {
            return false;
        }
        return this.loadedBillingStatus != null && LOCKED_BILLING_STATUSES.has(this.loadedBillingStatus);
    }

    @mobx.action
    private setLoadedBillingStatus(billingStatus: string | null): void {
        this.loadedBillingStatus = billingStatus;
    }

    public isCreationOrderValid(): boolean {
        if (this.isLinkMode) {
            return Boolean(this.existingOrderValue)
                && this.linkedOrder !== null
                && !this.existingOrderLoading
                && !this.selectedOrderAlreadyLinked;
        }
        return !this.isCloneFieldsLocked;
    }

    @mobx.action.bound
    private setExistingOrderValue(value: string): void {
        this.existingOrderValue = value;
    }

    @mobx.action.bound
    private setExistingOrderTouched(): void {
        this.existingOrderTouched = true;
    }

    public getExistingOrderFieldProps = (): FormFieldProps => {
        const alreadyLinked = this.isLinkedOrderAlreadyLinked;
        const selectionPending = this.isLinkMode
            && this.existingOrderLoading
            && Boolean(this.existingOrderValue)
            && !alreadyLinked;
        const valid = this.isCreationOrderValid() || selectionPending;
        return {
            value: this.existingOrderValue,
            valid: valid,
            touched: this.existingOrderTouched,
            setValue: value => this.setExistingOrderValue((value as string) ?? ''),
            setTouched: this.setExistingOrderTouched,
            validationMessage: alreadyLinked
                ? EXISTING_ORDER_ALREADY_LINKED_MESSAGE
                : (valid ? null : EXISTING_ORDER_REQUIRED_MESSAGE),
        };
    };

    private cachedRecentOrderOptions: { formData: ISalesforceOrderFormDataDTO | null; options: ISelectOption[] } | null = null;
    private lastSearchTerm = '';
    private lastSearchOptions: ISelectOption[] = [];

    public getRecentOrderOptions(): ISelectOption[] {
        if (this.cachedRecentOrderOptions?.formData !== this.formData) {
            const options = (this.formData?.recentOrders ?? []).map(toRecentOrderOption);
            this.cachedRecentOrderOptions = { formData: this.formData, options: options };
        }
        return this.cachedRecentOrderOptions.options;
    }

    public getCachedRecentOrders = (): { term: string; options: ISelectOption[] } | null => {
        const options = this.getRecentOrderOptions();
        return options.length > 0 ? { term: '', options: options } : null;
    };

    public searchExistingOrders = async (term: string, signal?: AbortSignal): Promise<ISelectOption[]> => {
        if (signal?.aborted) {
            return [];
        }
        if (this.lastSearchTerm === term && this.lastSearchOptions.length > 0) {
            return this.lastSearchOptions;
        }
        const cacheKey = recentOrderSearchCacheKey(this.event.eventId);
        const cached = this.localStorageService.getLocalStorageValue<ILocalStorageResponse>(cacheKey);
        if (cached && cached.value && this.localStorageService.checkTimeRange(cached.creationDate, cacheKey, SF_RECENT_ORDER_SEARCH_CACHE_MINUTES)) {
            const entry = cached.value as IRecentOrderCache;
            if (entry.term === term) {
                const existingOrderOptions = entry.orders.map(toLookupOptionFromCache);
                this.lastSearchTerm = term;
                this.lastSearchOptions = existingOrderOptions;
                return existingOrderOptions;
            }
        }

        const recentOrders = await this.salesforceOrderDAO.searchRecentOrders({
            accountId: this.accountId,
            searchTerm: term,
            ...(signal ? { signal: signal } : {}),
        });
        if (signal?.aborted) {
            return [];
        }
        const options = recentOrders.map(toRecentOrderOption);
        const orders = recentOrders.map(toCachedRecentOrder).map(order => ({
            ...order,
        }));
        this.localStorageService.setLocalStorageValue({ name: cacheKey, value: { term: term, orders: orders }, creationDate: Date.now() });
        this.lastSearchTerm = term;
        this.lastSearchOptions = options;
        return options;
    };

    private findInMemoryExistingOrderOption(orderId: string): ISelectOption | undefined {
        const fromRecent = this.getRecentOrderOptions().find(option => option.value === orderId);
        const fromSearch = this.lastSearchOptions.find(option => option.value === orderId);
        if (isOrderAlreadyLinked(fromSearch)) {
            return fromSearch;
        }
        if (isOrderAlreadyLinked(fromRecent)) {
            return fromRecent;
        }
        return fromSearch ?? fromRecent;
    }

    private findCachedExistingOrderOption(orderId: string): ISelectOption | undefined {
        if (typeof this.localStorageService?.getLocalStorageValue !== 'function') {
            return undefined;
        }
        const cacheKey = recentOrderSearchCacheKey(this.event.eventId);
        const cached = this.localStorageService.getLocalStorageValue<ILocalStorageResponse>(cacheKey);
        if (!cached?.value || !this.localStorageService.checkTimeRange(cached.creationDate, cacheKey, SF_RECENT_ORDER_SEARCH_CACHE_MINUTES)) {
            return undefined;
        }
        const entry = cached.value as IRecentOrderCache;
        const cachedOrder = entry.orders.find(order => order.id === orderId);
        return cachedOrder ? toLookupOptionFromCache(cachedOrder) : undefined;
    }

    private findExistingOrderOption(orderId: string): ISelectOption | undefined {
        return this.findInMemoryExistingOrderOption(orderId) ?? this.findCachedExistingOrderOption(orderId);
    }

    private resolveSelectedOrderNumber(orderId: string, option?: ISalesforceOrderLookupOption): string {
        const fromOption = orderNumberFromOption(option);
        if (fromOption) {
            return fromOption;
        }
        const fromRecent = (this.formData?.recentOrders ?? []).find(order => order.id === orderId)?.orderNumber;
        if (fromRecent && String(fromRecent).trim()) {
            return String(fromRecent).trim();
        }
        return orderNumberFromOption(this.findExistingOrderOption(orderId));
    }

    private existingOrderAbort: AbortController | null = null;

    @mobx.action
    private cancelExistingOrderRequest(): void {
        this.existingOrderAbort?.abort();
        this.existingOrderAbort = null;
        this.existingOrderLoading = false;
    }

    @mobx.action.bound
    public async onExistingOrderSelected(orderId: string, option?: ISalesforceOrderLookupOption): Promise<void> {
        this.beginExistingOrderSelection(orderId, option);
        this.cancelExistingOrderRequest();
        if (!orderId) {
            return;
        }
        const controller = new AbortController();
        this.existingOrderAbort = controller;
        this.existingOrderLoading = true;
        try {
            const order = await this.salesforceOrderDAO.getOrderForEvent({
                accountId: this.accountId,
                eventId: this.event.eventId,
                orderId: orderId,
                signal: controller.signal,
            });
            if (controller.signal.aborted || this.existingOrderValue !== orderId) {
                return;
            }
            if (this.creationMode === 'link') {
                this.applyLinkedOrderSelection(option, order);
                return;
            }
            if (this.creationMode === 'clone') {
                await this.applyOrderToForm(order);
            }
        } catch {
            if (controller.signal.aborted) {
                return;
            }
            this.failExistingOrderSelection(orderId);
        } finally {
            if (this.existingOrderAbort === controller) {
                this.existingOrderAbort = null;
                this.setExistingOrderLoading(false);
            }
        }
    }

    @mobx.action
    private beginExistingOrderSelection(orderId: string, option?: ISalesforceOrderLookupOption): void {
        this.existingOrderValue = orderId;
        this.existingOrderTouched = true;
        if (!orderId) {
            this.clearExistingOrderSelection();
            return;
        }
        this.setLinkedOrder(null);
        this.selectedOrderNumber = this.resolveSelectedOrderNumber(orderId, option);
        this.setSelectedOrderAlreadyLinked(
            this.creationMode === 'link'
                && (isOrderAlreadyLinked(option) || isOrderAlreadyLinked(this.findExistingOrderOption(orderId))),
        );
    }

    @mobx.action
    private clearExistingOrderSelection(): void {
        this.resetVm();
        this.setLinkedOrder(null);
        this.setSelectedOrderAlreadyLinked(false);
        this.selectedOrderNumber = '';
        this.products = [];
        this.priceBookId = null;
        this.manageProductsVm = null;
    }

    @mobx.action
    private applyLinkedOrderSelection(option: ISalesforceOrderLookupOption | undefined, order: ISalesforceOrderDTO): void {
        if (!order?.order) {
            this.existingOrderValue = '';
            this.setLinkedOrder(null);
            this.setSelectedOrderAlreadyLinked(false);
            this.selectedOrderNumber = '';
            showNotification(this.intl.formatMessage(EXISTING_ORDER_LINK_LOAD_ERROR_TEXT));
            return;
        }
        this.setLinkedOrder(order);
        this.setSelectedOrderAlreadyLinked(
            isOrderAlreadyLinked(option)
                || isOrderAlreadyLinked(this.findExistingOrderOption(this.existingOrderValue))
                || isOrderAlreadyLinked(order),
        );
    }

    @mobx.action
    private failExistingOrderSelection(orderId: string): void {
        if (this.existingOrderValue !== orderId) {
            return;
        }
        this.existingOrderValue = '';
        this.setLinkedOrder(null);
        this.setSelectedOrderAlreadyLinked(false);
        this.selectedOrderNumber = '';
        const loadError = this.creationMode === 'link'
            ? EXISTING_ORDER_LINK_LOAD_ERROR_TEXT
            : EXISTING_ORDER_CLONE_ERROR_TEXT;
        showNotification(this.intl.formatMessage(loadError));
    }

    @mobx.action
    private setExistingOrderLoading(value: boolean): void {
        this.existingOrderLoading = value;
    }

    public get isLoadingFormData(): boolean {
        return this.loadingFormData;
    }

    @mobx.action
    private async loadFormData(): Promise<void> {
        this.loadingFormData = true;
        try {
            const data = await this.salesforceOrderDAO.getOrderFormData({
                accountId: this.accountId,
                eventId: this.event.eventId,
            });
            this.setFormData(data);
        } catch {
            // Leave the dropdowns empty if the lookup fetch fails.
        } finally {
            this.stopLoadingFormData();
        }
    }

    @mobx.action
    private setFormData(data: ISalesforceOrderFormDataDTO): void {
        this.formData = data;
    }

    @mobx.action
    private stopLoadingFormData(): void {
        this.loadingFormData = false;
    }

    public getContractOptions(): ISelectOption[] {
        return toIdNameOptions(this.formData?.contracts);
    }

    public getParentOrderOptions(): ISelectOption[] {
        const options = (this.formData?.multiEventOrders ?? []).map(o => ({ value: o.id, label: o.orderNumber }));
        return this.mergeOrderLookups(options, [this.order?.order?.parentOrder]);
    }

    public getContactOptions(): ISelectOption[] {
        const options = toIdNameOptions(this.formData?.contacts);
        return this.mergeOrderLookups(options, [
            this.order?.order?.requesterContact,
            this.order?.order?.billingContact,
            this.order?.order?.additionalRecipient1,
            this.order?.order?.additionalRecipient2,
        ]);
    }

    public getEventTypeOptions(): ISelectOption[] {
        const options = (this.formData?.eventType ?? []).map(type => ({ value: type, label: type }));
        const current = this.stringValue('eventType');
        if (current && !options.some(option => option.value === current)) {
            return [...options, { value: current, label: current }];
        }
        return options;
    }

    private mergeOrderLookups(options: ISelectOption[], lookups: (ISalesforceLookupDTO | null | undefined)[]): ISelectOption[] {
        const byValue = new Map(options.map(option => [option.value, option]));
        lookups
            .filter((lookup): lookup is ISalesforceLookupDTO => !!lookup?.id)
            .forEach(lookup => {
                if (!byValue.has(lookup.id)) {
                    byValue.set(lookup.id, { value: lookup.id, label: lookup.name });
                }
            });
        return Array.from(byValue.values());
    }

    private readonly lookupFields = createLookupFieldDispatch({
        corporateRelated: {
            get: () => this.corporateRelatedOption,
            set: option => { this.corporateRelatedOption = option; },
        },
        billToLegalEntity: {
            get: () => this.billToLegalEntityOption,
            set: option => { this.billToLegalEntityOption = option; },
        },
        channelAgencyReferral: {
            get: () => this.channelAgencyReferralOption,
            set: option => { this.channelAgencyReferralOption = option; },
        },
        opportunity: {
            get: () => this.opportunityOption,
            set: option => { this.opportunityOption = option; },
        },
    });

    public getCorporateRelatedInitialOption(): ISelectOption | null {
        return this.lookupFields.getInitialOption('corporateRelated');
    }

    public getBillToLegalEntityInitialOption(): ISelectOption | null {
        return this.lookupFields.getInitialOption('billToLegalEntity');
    }

    public getOpportunityInitialOption(): ISelectOption | null {
        return this.lookupFields.getInitialOption('opportunity');
    }

    public getAccountSearchInitialOption(fieldKey: string): ISelectOption | null {
        return this.lookupFields.getInitialOption(fieldKey);
    }

    @mobx.action.bound
    public onAccountSelected(fieldKey: string, option: ISelectOption | null): void {
        this.lookupFields.setOption(fieldKey, option);
    }

    private get selectedPriceBookId(): string | null {
        return this.priceBookId;
    }

    private getSelectedPriceBookName(): string {
        return (this.getValue('priceBook') as string) || '';
    }

    @mobx.action
    private selectContact(contactKey: string, emailKey: string, value: FormVmValueType): ISalesforceContactDTO | undefined {
        this.setValue(contactKey, value);
        const contact = (this.formData?.contacts ?? []).find(c => c.id === value);
        this.setValue(emailKey, contact?.email ?? '');
        return contact;
    }

    private formatMailingAddress(address: ISalesforceAddressDTO | null | undefined): string {
        if (!address) {
            return '';
        }
        const cityLine = [address.city, address.state, address.postalCode].filter(Boolean).join(' ');
        return [address.street, cityLine, address.country].filter(part => !!part && part.trim() !== '').join('\n');
    }

    @mobx.action.bound
    public setBillingContact(value: FormVmValueType): void {
        const contact = this.selectContact('billingContact', 'billingContactEmail', value);
        this.setValue('billingAddress', this.formatMailingAddress(contact?.mailingAddress));
    }

    @mobx.action.bound
    public setAdditionalRecipient1(value: FormVmValueType): void {
        this.selectContact('additionalRecipient1', 'additionalRecipient1Email', value);
    }

    @mobx.action.bound
    public setAdditionalRecipient2(value: FormVmValueType): void {
        this.selectContact('additionalRecipient2', 'additionalRecipient2Email', value);
    }

    private async loadBlueprintName(): Promise<void> {
        const blueprintId = this.event?.blueprintId;
        if (!blueprintId || this.accountId == null) {
            return;
        }
        try {
            const blueprint = await this.blueprintsDAO.getBlueprint({ accountId: this.accountId, blueprintId: blueprintId });
            if (blueprint?.name) {
                this.setBlueprintName(blueprint.name);
            }
        } catch {
            // Leave the fallback (topic/id) if the blueprint can't be fetched.
        }
    }

    @mobx.action
    private setBlueprintName(name: string): void {
        this.blueprintName = name;
    }

    public get intl(): IntlShape {
        return this.intlProvider.getIntl();
    }

    public get isSaving(): boolean {
        return this.saving;
    }


    public getEventName(): string {
        return this.event?.name ?? '';
    }

    public getEventDateLines(): string[] {
        const { startDateTime, endDateTime, timeZone } = this.event ?? {};
        if (!startDateTime || !timeZone) {
            return [];
        }
        const formatter = IoC.Container.get(IDateTimeFormatter);
        const format = (date: Date): string =>
            formatter.dateToFormattedString(date, DateFnsFormats.StringifiedDayMonthNumericDateYear, timeZone);
        const start = format(startDateTime);
        const end = endDateTime ? format(endDateTime) : null;
        if (!end || end === start) {
            return [start];
        }
        return [start, end];
    }

    public getEventWhenLines(): string[] {
        return [...this.getEventDateLines(), this.getEventTimeRangeLabel(), this.getEventTimezoneLabel()].filter(Boolean);
    }

    public getEventTimeRangeLabel(): string {
        const { startDateTime, endDateTime, timeZone } = this.event ?? {};
        if (!startDateTime || !timeZone) {
            return '';
        }
        const formatter = IoC.Container.get(IDateTimeFormatter);
        const toTime = (date: Date): string =>
            formatter.dateToFormattedString(date, TimeFormats.Day12Hours, timeZone)
                .replace('AM', 'am')
                .replace('PM', 'pm');
        return endDateTime ? `${toTime(startDateTime)} - ${toTime(endDateTime)}` : toTime(startDateTime);
    }

    public getEventTimezoneLabel(): string {
        const { timeZone } = this.event ?? {};
        if (!timeZone) {
            return '';
        }
        const offset = `GMT${formatTimezoneOffset(getTimezoneOffsetInMinutes(timeZone))}`;
        return `(${offset}) ${timezonesCodeLabelMap[timeZone] || timeZone}`;
    }

    public getBlueprint(): string {
        return this.blueprintName || this.event?.blueprintInputs?.topic || this.event?.blueprintId || EMPTY;
    }

    public getRegion(): string {
        return this.event?.region || EMPTY;
    }

    public getEventType(): string {
        return this.event?.type || EMPTY;
    }

    public getClientDepartment(): string {
        return this.event?.department || EMPTY;
    }

    public getProjectManager(): string {
        const [pm] = resolveSalesforceProjectManagers(this.event?.deliveryTeamMembers);
        if (!pm) {
            return EMPTY;
        }
        const name = pm.fullName || `${pm.firstName ?? ''} ${pm.lastName ?? ''}`.trim();
        return name || EMPTY;
    }

    public getProjectManager2(): string {
        const [, pm2] = resolveSalesforceProjectManagers(this.event?.deliveryTeamMembers);
        if (!pm2) {
            return EMPTY;
        }
        const name = pm2.fullName || `${pm2.firstName ?? ''} ${pm2.lastName ?? ''}`.trim();
        return name || EMPTY;
    }

    public hasProjectManager2(): boolean {
        const [, pm2] = resolveSalesforceProjectManagers(this.event?.deliveryTeamMembers);
        return !!pm2;
    }


    public getOrderSubtypeOptions(): string[] {
        const orderType = this.getValue('orderType') as string;
        return ORDER_SUBTYPE_BY_TYPE[orderType] ?? [];
    }

    @mobx.action.bound
    public setOrderType(value: FormVmValueType): void {
        applyOrderTypeChange(this, value);
    }

    @mobx.action.bound
    public setCancellationReason(value: FormVmValueType): void {
        applyCancellationReasonChange(this, value);
    }

    @mobx.action.bound
    public setHighProfileReasoning(value: FormVmValueType): void {
        applyHighProfileReasoningChange(this, value);
    }

    @mobx.action.bound
    public setHighProfileOrder(value: FormVmValueType): void {
        applyHighProfileOrderChange(this, value);
    }

    @mobx.action.bound
    public setBillingMethod(value: FormVmValueType): void {
        applyBillingMethodChange(this, value);
    }

    @mobx.action.bound
    public setContractNumber(value: FormVmValueType): void {
        this.setValue('contractNumber', value);
        const contract = (this.formData?.contracts ?? []).find(c => c.id === value);
        const nextPriceBookId = contract?.pricebook?.id ?? null;
        if (nextPriceBookId !== this.priceBookId) {
            this.products = [];
            this.manageProductsVm = null;
        }
        this.setValue('priceBook', contract?.pricebook?.name ?? '');
        this.priceBookId = nextPriceBookId;
        this.setValue('currency', contract?.currencyIsoCode ?? '');
    }

    public get productCount(): number {
        return this.products.length;
    }

    public getManageProductsVm(): ManageProductsViewModel | null {
        return this.manageProductsVm;
    }

    public getProducts(): IManageProductsSelection[] {
        return this.products;
    }

    @mobx.action.bound
    public openManageProducts(): void {
        if (this.isBillingLocked) {
            return;
        }
        if (!this.selectedPriceBookId) {
            showNotification(this.intl.formatMessage(MANAGE_PRODUCTS_NO_PRICE_BOOK_TEXT));
            return;
        }
        this.manageProductsVm = new ManageProductsViewModel({
            contractId: (this.getValue('contractNumber') as string) || null,
            priceBookId: this.selectedPriceBookId,
            priceBookName: this.getSelectedPriceBookName(),
            currency: (this.getValue('currency') as string) || DEFAULT_CURRENCY,
            initialSelections: this.products,
            onApply: this.applyProducts,
            onClose: this.closeManageProducts,
        });
        this.isManageProductsOpen = true;
    }

    @mobx.action.bound
    public closeManageProducts(): void {
        this.isManageProductsOpen = false;
    }

    @mobx.action.bound
    public applyProducts(selections: IManageProductsSelection[], currency: string): void {
        const previous = this.products;
        this.products = selections;
        this.setValue('currency', currency);
        this.trackProductsUpdated(previous, selections);
    }

    private trackProductsUpdated(previous: IManageProductsSelection[], current: IManageProductsSelection[]): void {
        this.trackingService.get().track(
            SALESFORCE_ORDER_TRACKING_EVENTS.SF_ORDER_PRODUCTS_UPDATED,
            ManageProductsTrackingMapper.toTrackingEvent({
                event: this.event,
                orderId: this.event?.orderId ?? null,
                priceBookName: this.getSelectedPriceBookName(),
                previous: previous,
                current: current,
            }),
        );
    }


    private collectValues(): Record<string, string> {
        return this.getVmState().reduce((acc, item) => {
            acc[item.name] = item.value == null ? '' : String(item.value);
            return acc;
        }, {} as Record<string, string>);
    }

    private stringValue(key: string): string {
        return readStringValue(this.getValue(key));
    }


    private booleanValue(key: string): boolean {
        return readBooleanValue(this.getValue(key));
    }

    private toIsoString(value: Date | string | undefined): string {
        if (!value) {
            return '';
        }
        const date = value instanceof Date ? value : new Date(value);
        return Number.isNaN(date.getTime()) ? '' : date.toISOString();
    }

    private buildEventPayload(): ISalesforceOrderCreateEvent {
        const allowedRecordTypeName = toAllowedRecordTypeName(this.recordType);
        return {
            orderId: this.event?.orderId ?? '',
            accountId: this.formData?.accountId ?? '',
            ...(this.isEditMode && this.hasSalesforceRecordType && allowedRecordTypeName
                ? { recordTypeName: allowedRecordTypeName }
                : {}),
            ...(this.isEditMode && isNoContractRequiredRecordType(this.recordType)
                ? {}
                : { contractId: this.stringValue('contractNumber') }),
            name: this.event?.name ?? '',
            startDateTime: this.toIsoString(this.event?.startDateTime),
            endDateTime: this.toIsoString(this.event?.endDateTime),
            timeZone: this.event?.timeZone ?? '',
            region: this.event?.region ?? '',
            eventType: this.stringValue('eventType'),
            department: this.event?.department ?? '',
            oecEventId: this.event?.eventId != null ? String(this.event.eventId) : '',
            externalEventId: this.event?.externalId ?? '',
            oecUrl: '',
        };
    }

    private buildPayload(): ISalesforceOrderCreatePayload {
        const products: ISalesforceOrderCreateProduct[] = this.products.map(product => ({
            pricebookEntryId: product.pricebookEntryId,
            ...(product.lineItemId ? { lineItemId: product.lineItemId } : {}),
            quantity: product.quantity,
            unitPrice: product.unitPrice,
            notes: product.notes,
        }));
        return {
            orderStatus: this.stringValue('orderStatus'),
            billingStatus: this.stringValue('billingStatus'),
            multiEventOrder: this.stringValue('multiEventOrder'),
            parentOrder: this.stringValue('parentOrder'),
            requesterContact: this.stringValue('requesterContact'),
            corporateRelated: this.stringValue('corporateRelated'),
            opportunityId: this.stringValue('opportunity'),
            channelAgencyReferral: this.stringValue('channelAgencyReferral'),
            hostLob: this.stringValue('hostLob'),
            hostSubLob: this.stringValue('hostSubLob'),
            billToLegalEntity: this.stringValue('billToLegalEntity'),
            orderType: this.stringValue('orderType'),
            orderSubtype: this.stringValue('orderSubtype'),
            cancellationFee: this.stringValue('cancellationFee'),
            cancellationReason: this.stringValue('cancellationReason'),
            cancellationOther: this.stringValue('cancellationOther'),
            syncToTeams: this.booleanValue('syncToTeams'),
            noStaffRequired: this.booleanValue('noStaffRequired'),
            hybridEvent: this.stringValue('hybridEvent'),
            eventTypeClient: this.stringValue('eventTypeClient'),
            highProfileOrder: this.booleanValue('highProfileOrder'),
            highProfileReasoning: this.stringValue('highProfileReasoning'),
            highProfileAttendeeDetails: this.stringValue('highProfileAttendeeDetails'),
            accountLegalEntity: this.stringValue('accountLegalEntity'),
            isThisEventRepeatable: this.stringValue('isThisEventRepeatable'),
            billingTimeSensitivity: this.stringValue('billingTimeSensitivity'),
            billingContact: this.stringValue('billingContact'),
            additionalRecipient1: this.stringValue('additionalRecipient1'),
            additionalRecipient2: this.stringValue('additionalRecipient2'),
            billingMethod: this.stringValue('billingMethod'),
            clientCostCode: this.stringValue('clientCostCode'),
            secondaryPO: this.stringValue('secondaryPO'),
            billingNotes: this.stringValue('billingNotes'),
            qaChatEnabled: this.stringValue('qaChatEnabled'),
            qaReportSent: this.stringValue('qaReportSent'),
            eventRanSmoothly: this.booleanValue('eventRanSmoothly'),
            postEventNotes: this.stringValue('postEventNotes'),
            description: this.stringValue('description'),
            pricebookId: this.selectedPriceBookId,
            currencyIsoCode: this.stringValue('currency'),
            products: products,
            ...this.buildEventPayload(),
        };
    }

    public override isVmValid = (): boolean => {
        if (this.isLinkMode) {
            return true;
        }
        if (isNoContractRequiredRecordType(this.recordType)) {
            return this.isVmValidExcept('contractNumber');
        }

        return this.isVmValidExcept();
    };

    @mobx.action.bound
    public async submit(): Promise<boolean> {
        if (this.saving) {
            return false;
        }
        if (this.isLinkMode) {
            this.existingOrderTouched = true;
            if (!this.isCreationOrderValid()) {
                return false;
            }
            return this.persistLinkedOrder();
        }
        if (!this.isVmValid() || !this.isCreationOrderValid()) {
            this.setVmTouched();
            this.existingOrderTouched = true;
            return false;
        }
        const errorText = this.isEditMode ? ORDER_UPDATE_ERROR_TEXT : ORDER_CREATE_ERROR_TEXT;
        this.setSaving(true);
        this.setSaveError(null);
        try {
            const result = this.isEditMode
                ? await this.salesforceOrderDAO.updateOrder({
                    accountId: this.accountId,
                    eventId: this.event.eventId,
                    orderId: this.event.orderId ?? '',
                    payload: this.buildPayload(),
                })
                : await this.salesforceOrderDAO.createOrder({
                    accountId: this.accountId,
                    eventId: this.event.eventId,
                    payload: this.buildPayload(),
                });
            if (!result?.success) {
                this.setSaveError(result?.errorMessage || this.intl.formatMessage(errorText));
                return false;
            }
            if (this.isEditMode) {
                this.setLoadedBillingStatus(this.stringValue('billingStatus'));
            }
            this.track(this.collectValues(), result);
            showNotification(this.intl.formatMessage(this.isEditMode ? ORDER_UPDATED_TEXT : ORDER_ADDED_TEXT));
            this.onCreated(result);
            return true;
        } catch (error) {
            console.error('Failed to save Salesforce order', error);
            this.setSaveError(this.resolveRequestError(error, errorText));
            return false;
        } finally {
            this.setSaving(false);
        }
    }

    @mobx.action.bound
    private async persistLinkedOrder(): Promise<boolean> {
        this.setSaving(true);
        this.setSaveError(null);
        try {
            const orderNumber = (this.selectedOrderNumber || this.resolveSelectedOrderNumber(this.existingOrderValue)).trim();
            if (!orderNumber) {
                this.setSaveError(this.intl.formatMessage(ORDER_LINK_ERROR_TEXT));
                return false;
            }
            const result = await this.salesforceOrderDAO.linkOrder({
                accountId: this.accountId,
                eventId: this.event.eventId,
                orderId: this.existingOrderValue,
                orderNumber: orderNumber,
            });
            if (!result?.success) {
                this.setSaveError(result?.errorMessage || this.intl.formatMessage(ORDER_LINK_ERROR_TEXT));
                return false;
            }
            this.trackLink(result);
            showNotification(this.intl.formatMessage(ORDER_LINKED_TEXT));
            this.onCreated(result);
            return true;
        } catch (error) {
            this.setSaveError(this.resolveRequestError(error, ORDER_LINK_ERROR_TEXT));
            return false;
        } finally {
            this.setSaving(false);
        }
    }

    private resolveRequestError(error: unknown, fallback: MessageDescriptor): string {
        const apiError = this.errorParsing.extractAPIError(error);
        if (!apiError) {
            return this.intl.formatMessage(fallback);
        }
        const fieldMessages = Array.from(new Set((apiError.fields ?? []).map(field => field.message).filter(Boolean)));
        if (fieldMessages.length) {
            return fieldMessages.join(' ');
        }
        const isParsedMessage = apiError.errorMessage && apiError.errorMessage !== (error as IResponseBase).rawResponse;
        return isParsedMessage ? apiError.errorMessage : this.intl.formatMessage(fallback);
    }

    public get saveError(): string | null {
        return this.saveErrorMessage;
    }

    @mobx.action
    private setSaveError(message: string | null): void {
        this.saveErrorMessage = message;
    }

    @mobx.action
    private setSaving(value: boolean): void {
        this.saving = value;
    }

    private lookupTrackingName(key: string, option: ISelectOption | null, fallback?: ISalesforceLookupDTO | null): string {
        if (!this.stringValue(key)) {
            return '';
        }
        if (option?.label) {
            return option.label;
        }
        return fallback?.name ?? '';
    }

    private track(values: Record<string, string>, result: ISalesforceOrderCreateResult): void {
        const trackedValues = {
            ...values,
            opportunity: this.lookupTrackingName('opportunity', this.opportunityOption, this.order?.order?.opportunityName),
            channelAgencyReferral: this.lookupTrackingName(
                'channelAgencyReferral',
                this.channelAgencyReferralOption,
                this.order?.order?.channelAgencyReferral,
            ),
        };
        this.trackingService.get().track(
            this.isEditMode
                ? SALESFORCE_ORDER_TRACKING_EVENTS.EDIT_SALESFORCE_ORDER
                : SALESFORCE_ORDER_TRACKING_EVENTS.CREATE_SALESFORCE_ORDER,
            AddSalesforceOrderTrackingMapper.toTrackingEvent(
                this.event,
                trackedValues,
                {
                    productCount: this.products.length,
                    order: {
                        orderId: result.orderId,
                        orderNumber: result.orderNumber,
                    },
                    billingConfirmation: this.order?.order?.billingConfirmation ?? null,
                    recordType: this.recordType,
                },
            ),
        );
    }

    private trackLink(result: ISalesforceOrderCreateResult): void {
        this.trackingService.get().track(
            SALESFORCE_ORDER_TRACKING_EVENTS.LINK_SALESFORCE_ORDER,
            AddSalesforceOrderTrackingMapper.toLinkTrackingEvent(this.event, {
                orderId: result.orderId,
                orderNumber: result.orderNumber,
                productCount: this.linkedOrder?.products?.length ?? 0,
            }),
        );
    }
}

export { AddSalesforceOrderFormViewModel };
